import { useEffect, useMemo } from "react";
import useAuth from "./useAuth";
import { InitiateTopupTriggers } from "../jotai/atoms/analytics";
import { statsigClient } from "../config/statsig";
import * as analyticsUtils from "../utils/analytics";
import posthog from "posthog-js";

export default function useAnalytics() {
  const { isLoggedIn, userProfile } = useAuth();

  // Attribute user ID to events
  useEffect(() => {
    const { id: userId, email } = userProfile ?? {};

    // AppsFlyer: Disabled for now
    // window.AF("pba", "setCustomerUserId", userId ?? "");

    // Statsig
    (async () =>
      await statsigClient.updateUserAsync({
        userID: userId,
      }))();

    if (isLoggedIn) {
      posthog.identify(userId, { email });
      posthog.reloadFeatureFlags();
      return;
    }
    posthog.reset();
  }, [userProfile, isLoggedIn]);

  const userProperties = useMemo(() => {
    const { id, email, username, currency, countryCode } = userProfile ?? {};
    const userPiiProperties: analyticsUtils.UserPIIProperties = {
      email: email,
      userid: id,
      username: username,
    };
    const userNonPiiProperties: analyticsUtils.UserNonPIIProperties = {
      user_currency: currency,
      user_country: countryCode,
    };
    return {
      pii: userPiiProperties,
      nonPii: userNonPiiProperties,
      all: { ...userPiiProperties, ...userNonPiiProperties },
    };
  }, [userProfile]);

  const analytics = useMemo(() => {
    return {
      sendDownloadAppOptionsEvent: (
        download_app_option: analyticsUtils.DownloadAppOptionProperty,
      ) => {
        analyticsUtils.registerDownloadAppOptionsEvent({
          ...userProperties.all,
          user_status: isLoggedIn
            ? analyticsUtils.UserStatusProperty.LOGGED_IN
            : analyticsUtils.UserStatusProperty.LOGGED_OUT,
          download_app_option,
        });
      },

      sendFirstOnboardingScreenEvent: () => {
        analyticsUtils.registerFirstOnboardingScreenEvent(userProperties.pii);
      },

      sendSignupOptionsEvent: (
        signup_option: analyticsUtils.SignupOptionProperty,
      ) => {
        analyticsUtils.registerSignupOptionsEvent({
          ...userProperties.pii,
          signup_option,
        });
      },

      sendChooseRewardSelectionEvent: (hrp_earnings_target: number) => {
        analyticsUtils.registerChooseRewardSelectionEvent({
          ...userProperties.pii,
          hrp_earnings_target,
        });
      },

      sendSuccessfulSignupEvent: (
        signup_method: analyticsUtils.SignupMethodProperty,
        userPiiProperties: analyticsUtils.UserPIIProperties,
      ) => {
        analyticsUtils.registerSuccessfulSignupEvent({
          ...userPiiProperties,
          signup_method,
        });
      },

      sendSuccessfulSigninEvent: (
        signin_method: analyticsUtils.SignupMethodProperty,
        userPiiProperties: analyticsUtils.UserPIIProperties,
      ) => {
        analyticsUtils.registerSuccessfulSigninEvent({
          ...userPiiProperties,
          signin_method,
        });
      },

      sendFailedSignupEvent: (
        props: Omit<
          analyticsUtils.FailedSignupEventProperties,
          keyof analyticsUtils.UserPIIProperties
        >,
      ) => {
        analyticsUtils.registerFailedSignupEvent({
          ...userProperties.pii,
          ...props,
        });
      },

      sendReferralOptionsEvent: (
        props: Omit<
          analyticsUtils.ReferralOptionsEventProperties,
          keyof analyticsUtils.UserPIIProperties
        >,
      ) => {
        analyticsUtils.registerReferralOptionsEvent({
          ...userProperties.pii,
          ...props,
        });
      },

      sendReferredByEvent: (
        props: Omit<
          analyticsUtils.ReferredByEventProperties,
          keyof analyticsUtils.UserPIIProperties
        >,
      ) => {
        analyticsUtils.registerReferredByEvent({
          ...userProperties.pii,
          ...props,
        });
      },

      sendClaimProfileEvent: (username?: string) => {
        analyticsUtils.registerClaimProfileEvent({
          ...userProperties.pii,
          username,
        });
      },

      sendStartGamingEvent: () => {
        analyticsUtils.registerStartGamingEvent(userProperties.pii);
      },

      sendFindFriendOptionsEvent: (
        find_friends_option: analyticsUtils.FindFriendsOptionProperty,
      ) => {
        analyticsUtils.registerFindFriendOptionsEvent({
          ...userProperties.pii,
          find_friends_option,
        });
      },

      sendNotificationOptionsEvent: (
        notification_option: analyticsUtils.NotificationOptionProperty,
      ) => {
        analyticsUtils.registerNotificationOptionsEvent({
          ...userProperties.pii,
          notification_option,
        });
      },

      sendSignupBonusOptionsEvent: (
        signup_bonus_option: analyticsUtils.SignupBonusOptionProperty,
      ) => {
        analyticsUtils.registerSignupBonusOptionsEvent({
          ...userProperties.pii,
          signup_bonus_option,
        });
      },

      sendSelectCurrencyEvent: (
        user_currency: analyticsUtils.UserNonPIIProperties["user_currency"],
      ) => {
        analyticsUtils.registerSelectCurrencyEvent({
          ...userProperties.pii,
          user_currency,
        });
      },

      sendSelectDobEvent: (
        props: Omit<
          analyticsUtils.SelectDobEventProperties,
          keyof analyticsUtils.UserPIIProperties
        >,
      ) => {
        analyticsUtils.registerSelectDobEvent({
          ...userProperties.pii,
          ...props,
        });
      },

      sendAllowLocationOptionsEvent: (
        allow_location_option: analyticsUtils.AllowLocationOptionProperty,
      ) => {
        analyticsUtils.registerAllowLocationOptionsEvent({
          ...userProperties.all,
          allow_location_option,
        });
      },

      sendInitiateTopupEvent: (button_name?: InitiateTopupTriggers) => {
        analyticsUtils.registerInitiateTopupEvent({
          ...userProperties.all,
          button_name,
        });
      },

      sendContinueTopupEvent: (
        props: Omit<
          analyticsUtils.ContinueTopupEventProperties,
          keyof analyticsUtils.UserProperties
        >,
      ) => {
        analyticsUtils.registerContinueTopupEvent({
          ...userProperties.all,
          ...props,
        });
      },

      sendCompleteTopupEvent: (
        props: Omit<
          analyticsUtils.CompleteTopupEventProperties,
          keyof analyticsUtils.UserProperties
        >,
      ) => {
        analyticsUtils.registerCompleteTopupEvent({
          ...userProperties.all,
          ...props,
        });
      },

      sendSuccessfulTopupEvent: (
        props: Omit<
          analyticsUtils.CompleteTopupEventProperties,
          keyof analyticsUtils.UserProperties
        >,
      ) => {
        analyticsUtils.registerSuccessfulTopupEvent({
          ...userProperties.all,
          ...props,
        });
      },

      sendInitiateGameEvent: (
        props: Omit<
          analyticsUtils.InitiateGameEventProperties,
          keyof analyticsUtils.UserProperties
        >,
      ) => {
        analyticsUtils.registerInitiateGameEvent({
          ...userProperties.all,
          ...props,
        });
      },

      sendCancelGameEvent: (
        props: Omit<
          analyticsUtils.GenericGameEventProperties,
          keyof analyticsUtils.UserProperties
        >,
      ) => {
        analyticsUtils.registerCancelGameEvent({
          ...userProperties.all,
          ...props,
        });
      },

      sendCompleteGameEvent: (
        props: Omit<
          analyticsUtils.CompleteGameEventProperties,
          keyof analyticsUtils.UserProperties
        >,
      ) => {
        analyticsUtils.registerCompleteGameEvent({
          ...userProperties.all,
          ...props,
        });
      },

      sendShareResultsEvent: (
        props: Omit<
          analyticsUtils.CompleteGameEventProperties,
          keyof analyticsUtils.UserProperties | "hpr_reward"
        >,
      ) => {
        analyticsUtils.registerShareResultsEvent({
          ...userProperties.all,
          ...props,
        });
      },

      sendGoHomeEvent: (
        props: Omit<
          analyticsUtils.CompleteGameEventProperties,
          keyof analyticsUtils.UserProperties | "hpr_reward"
        >,
      ) => {
        analyticsUtils.registerGoHomeEvent({ ...userProperties.all, ...props });
      },

      sendInitiateCashoutEvent: (hpr_wallet_balance: number) => {
        analyticsUtils.registerInitiateCashoutEvent({
          ...userProperties.all,
          hpr_wallet_balance,
        });
      },

      sendCompleteCashoutEvent: (
        props: Omit<
          analyticsUtils.CompleteCashoutEventProperties,
          keyof analyticsUtils.UserProperties
        >,
      ) => {
        analyticsUtils.registerCompleteCashoutEvent({
          ...userProperties.all,
          ...props,
        });
      },

      sendInitiateVerificationEvent: () => {
        analyticsUtils.registerInitiateVerificationEvent({
          ...userProperties.pii,
          user_country: userProperties.nonPii.user_country,
        });
      },

      sendCompleteVerificationEvent: (
        props: Omit<
          analyticsUtils.CompleteVerificationEventProperties,
          keyof analyticsUtils.UserProperties
        >,
      ) => {
        analyticsUtils.registerCompleteVerificationEvent({
          ...userProperties.pii,
          user_country: userProperties.nonPii.user_country,
          ...props,
        });
      },

      sendShareReferralLinkEvent: () => {
        analyticsUtils.registerShareReferralLinkEvent(userProperties.all);
      },

      sendInitiatingTopupPageOpenedEvent: (
        props: Omit<
          analyticsUtils.InitiatingTopupPageOpenedEventProperties,
          keyof analyticsUtils.UserPIIProperties
        >,
      ) => {
        analyticsUtils.registerInitiatingTopupPageOpenedEvent({
          ...userProperties.pii,
          ...props,
        });
      },

      sendPaystackRedirectEvent: (
        props: Omit<
          analyticsUtils.PaystackRedirectEventProperties,
          keyof analyticsUtils.UserPIIProperties
        >,
      ) => {
        analyticsUtils.registerPaystackRedirectEvent({
          ...userProperties.pii,
          ...props,
        });
      },
    };
  }, [isLoggedIn, userProperties]);

  return analytics;
}
